import { useEffect, useState } from 'react';
import { useAsyncList } from 'react-aria-components';
import {
  Autocomplete,
  type AutocompleteSuggestion,
} from '@components/sds/autocomplete/autocomplete';
import PillIcon from '@components/sds/icons/pill';
import { client } from '@services/graphql-client';
import type { DrugV2 } from 'generated-schema-types';
import DRUG_SEARCH from './drug-search.graphql';

interface DrugAutocompleteProps {
  selected: string | undefined;
  setSelected: (value: string | undefined) => void;
}

function distinctBy<T>(arr: T[], key: keyof T): T[] {
  const map = new Map<T[typeof key], T>();

  for (const item of arr) {
    map.set(item[key], item);
  }

  return Array.from(map.values());
}

export const DrugAutocomplete = ({
  selected,
  setSelected,
}: DrugAutocompleteProps) => {
  const [searching, setSearching] = useState(false);

  const list = useAsyncList<AutocompleteSuggestion>({
    async load({ signal, cursor, filterText }) {
      if (filterText && filterText !== selected) {
        setSearching(true);
        const response = await client.query({
          query: DRUG_SEARCH,
          variables: {
            after: cursor,
            where: { composite: { ilike: `${filterText}%` } },
          },
          context: { fetchOptions: { signal } }, // Support cancellation
        });
        setSearching(false);

        const filterTextUpper = filterText.toUpperCase();

        const distinctDrugNames = distinctBy<AutocompleteSuggestion>(
          response.data.getDrugs.edges?.map(({ node }: { node: DrugV2 }) => {
            if (node.ndc.toUpperCase().includes(filterTextUpper)) {
              return {
                label: `NDC ${node.ndc}`,
                description: node.drugName,
                value: `ndc=${node.ndc}`,
              };
            }

            if (node.gpi.toUpperCase().includes(filterTextUpper)) {
              return {
                label: `GPI ${node.ndc}`,
                description: node.drugName,
                value: `gpi=${node.gpi}`,
              };
            }

            return {
              label: node.drugName,
              value: `drugName=${node.drugName}`,
            };
          }),
          'value'
        );

        return {
          items: distinctDrugNames,
          cursor: response.data.getDrugs.pageInfo.endCursor, // For pagination [3]
        };
      }

      return {
        items: [],
        cursor: null,
      };
    },
  });

  const onFilterChange = (value: string) => {
    setSelected(undefined);
    list.setFilterText(value);
  };

  useEffect(() => {
    let selectedText = selected;

    if (!selected) {
      selectedText = '';
    }

    if (selectedText?.startsWith('ndc=') || selectedText?.startsWith('gpi=')) {
      selectedText = selectedText.substring(4);
    }

    if (selectedText?.startsWith('drugName=')) {
      selectedText = selectedText?.substring(9);
    }

    list.setFilterText(selectedText ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <Autocomplete
      inputValue={list.filterText}
      onInputChange={onFilterChange}
      aria-label="Search"
      loading={searching}
      setValue={setSelected}
      value={selected}
      filterText={list.filterText}
      placeholder="To search your MAC list enter a drug name, NDC, or GPI "
      items={list.items}
      startIcon={<PillIcon />}
    />
  );
};
